import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { FaPlus, FaSave, FaTimes, FaTrashAlt } from "react-icons/fa";
import { MdHome } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import {
    getData,
    postData,
    putData,
    deleteData,
} from "../services/FetchNodeservices"; // Assuming this is correct
import Header from "../components/Header"; // Assuming this is correct
import Swal from "sweetalert2";
import Footer from "../components/Footer";

// Insert item
const insertInvoiceItem = async (item, invoiceMasterId) => {
    const res = await postData("api/invoice_items", {
        invoice_master_id: invoiceMasterId,
        description: item.description,
        sac_code: item.sacCode || "998519",
        taxable_amount: item.amount,
        cgst: item.cgst || 0,
        sgst: item.sgst || 0,
        igst: item.igst || 0,
    });
    return res?.success ? res.id : null;
};


// Update item
const updateInvoiceItem = async (item) => {
    const res = await putData(`api/invoice_items/${item.id}`, {
        description: item.description,
        sac_code: item.sacCode || "998519",
        taxable_amount: item.amount,
        cgst: item.cgst || 0,
        sgst: item.sgst || 0,
        igst: item.igst || 0,
    });
    return res?.success;
};

// Delete item
const deleteInvoiceItem = async (id) => {
    const res = await deleteData(`api/invoice_items/${id}`);
    return res?.success;
};

// Fetch invoice master
const fetchInvoiceById = async (id) => {
    const res = await getData(`api/invoice_master/${id}`);
    return res?.success ? res.data : null;
};

// Fetch invoice items
const fetchInvoiceItemsByInvoiceMasterId = async (invoiceMasterId) => {
    const res = await getData(
        `api/invoice_items?invoice_master_id=${invoiceMasterId}`
    );
    return res?.success ? res.data : [];
};



const InvoiceForm = () => {
    const [invoiceData, setInvoiceData] = useState({
        recipientName: "",
        recipientGSTIN: "",
        recipientState: "",
        recipientAddress: "",
        invoiceNumber: "",
        invoiceDate: new Date().toISOString().slice(0, 10),
        invoiceType: "Inter State Invoice",
        paymentMode: "",
        serviceDescription: "",
        sacCode: "998519",
        taxableAmount: "",
        tax: 18,
        adjustment: 0,
        paymentDate: null,
        amountPaid: 0,
        status: "pending",
    });

    const [items, setItems] = useState([]);
    const [originalItems, setOriginalItems] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { invoiceId } = useParams();

    /* ================= FETCH COMPANIES ================= */
    useEffect(() => {
        (async () => {
            const res = await getData("api/company");
            if (Array.isArray(res?.data)) setCompanies(res.data);
            else if (Array.isArray(res)) setCompanies(res);
            else setCompanies([]);
        })();
    }, []);

    /* ================= AUTO INVOICE NUMBER ================= */
    useEffect(() => {
        if (!invoiceId) {
            (async () => {
                const data = await getData("api/invoice_master/latest");
                let latest = data?.latest_invoice || "MPC/25-26/0000";
                const parts = latest.split("/");
                const next =
                    parts.length === 3
                        ? `${parts[0]}/${parts[1]}/${String(
                            parseInt(parts[2]) + 1
                        ).padStart(4, "0")}`
                        : "MPC/25-26/0001";

                setInvoiceData((p) => ({ ...p, invoiceNumber: next }));
            })();
        }
    }, [invoiceId]);

    /* ================= FETCH EDIT DATA ================= */
    useEffect(() => {
        (async () => {
            if (!invoiceId) {
                setLoading(false);
                return;
            }

            const master = await fetchInvoiceById(invoiceId);
            const itemList = await fetchInvoiceItemsByInvoiceMasterId(invoiceId);

            if (!master) {
                Swal.fire("Error", "Invoice not found", "error");
                navigate("/invoive_master");
                return;
            }

            const subtotal = parseFloat(master.taxable_amount || 0);
            const totalTax =
                parseFloat(master.cgst || 0) +
                parseFloat(master.sgst || 0) +
                parseFloat(master.igst || 0);

            setInvoiceData((p) => ({
                ...p,
                recipientName: master.name,
                recipientGSTIN: master.gstin,
                recipientState: master.state,
                recipientAddress: master.company_address,
                invoiceNumber: master.invoice_number,
                invoiceDate: master.invoice_date?.slice(0, 10),
                tax: subtotal ? (totalTax / subtotal) * 100 : 18,
                status: master.status,
                paymentMode: master.payment_mode,
                amountPaid: master.amount_paid,
                paymentDate: master.payment_date?.slice(0, 10),
                invoiceType:
                    master.cgst > 0 || master.sgst > 0
                        ? "Intra State Invoice"
                        : "Inter State Invoice",
            }));

            const mappedItems = itemList.map((i) => ({
                id: i.id,
                description: i.description,
                sacCode: i.sac_code,
                amount: parseFloat(i.taxable_amount),
            }));

            setItems(mappedItems);
            setOriginalItems(mappedItems);
            setLoading(false);
        })();
    }, [invoiceId, navigate]);


    // ✅ Company select handler
    const handleCompanySelect = (e) => {
        const selectedName = e.target.value;
        const selectedCompany = companies.find(c => c.name === selectedName);
        if (selectedCompany) {
            setInvoiceData({
                ...invoiceData,
                recipientName: selectedCompany.name || "",
                recipientGSTIN: selectedCompany.gst_no || "",
                recipientState: selectedCompany.state || "",
                recipientAddress: selectedCompany.address || "",
            });
        } else {
            setInvoiceData({ ...invoiceData, recipientName: selectedName });
        }
    };

    // ✅ Add item (to local state)
    const addItem = () => {
        const { serviceDescription, sacCode, taxableAmount } = invoiceData;
        if (!serviceDescription || !taxableAmount || isNaN(parseFloat(taxableAmount)) || parseFloat(taxableAmount) <= 0) {
            Swal.fire("Missing Data", "Please fill description and a valid positive amount before adding.", "warning");
            return;
        }

        const newItem = {
            description: serviceDescription,
            sacCode: sacCode || "998519",
            amount: parseFloat(taxableAmount),
            cgst: 0,
            sgst: 0,
            igst: 0,
        };

        setItems([...items, newItem]);
        setInvoiceData({ ...invoiceData, serviceDescription: "", taxableAmount: "" });

        Swal.fire({
            title: "Item Added!",
            text: "Item added to list. Save the invoice to persist changes.",
            icon: "success",
            timer: 1200,
            showConfirmButton: false,
        });
    };

    // ✅ Remove item (from local state)
    const removeItem = async (index) => {
        const confirmResult = await Swal.fire({
            title: "Remove Item?",
            text: "Are you sure you want to delete this item?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, remove it",
            cancelButtonText: "No, keep it",
        });

        if (!confirmResult.isConfirmed) return;

        setItems(items.filter((_, i) => i !== index));

        Swal.fire({
            title: "Removed!",
            text: "Item removed from invoice list.",
            icon: "success",
            timer: 1000,
            showConfirmButton: false,
        });
    };


    // ✅ Calculations
    const subtotal = items.reduce((sum, i) => sum + i.amount, 0);
    const taxRate = parseFloat(invoiceData.tax) || 0;
    const taxAmount = (subtotal * taxRate) / 100;
    const adjustmentValue = parseFloat(invoiceData.adjustment || 0);
    const total = subtotal + taxAmount + adjustmentValue;


    // ✅ Save/Update Invoice to Backend (with items)
    const handleSave = async () => {
        try {
            if (!invoiceData.recipientName || !invoiceData.invoiceNumber) {
                Swal.fire(
                    "Missing Fields",
                    "Please fill recipient name and invoice number.",
                    "warning"
                );
                return;
            }

            if (items.length === 0) {
                Swal.fire(
                    "No Items",
                    "Please add at least one item to the invoice.",
                    "warning"
                );
                return;
            }

            const currentSubtotal = items.reduce(
                (sum, i) => sum + parseFloat(i.amount || 0),
                0
            );
            const currentTaxRate = parseFloat(invoiceData.tax) || 0;
            const currentTaxAmount = (currentSubtotal * currentTaxRate) / 100;
            const currentAdjustment = parseFloat(invoiceData.adjustment || 0);
            const currentTotal =
                currentSubtotal + currentTaxAmount + currentAdjustment;

            const payload = {
                invoice_number: invoiceData.invoiceNumber,
                name: invoiceData.recipientName,
                gstin: invoiceData.recipientGSTIN,
                state: invoiceData.recipientState,
                company_address: invoiceData.recipientAddress,
                invoice_date: invoiceData.invoiceDate,
                taxable_amount: currentSubtotal.toFixed(2),

                cgst:
                    invoiceData.invoiceType === "Intra State Invoice"
                        ? (currentTaxAmount / 2).toFixed(2)
                        : "0.00",

                sgst:
                    invoiceData.invoiceType === "Intra State Invoice"
                        ? (currentTaxAmount / 2).toFixed(2)
                        : "0.00",

                igst:
                    invoiceData.invoiceType === "Inter State Invoice"
                        ? currentTaxAmount.toFixed(2)
                        : "0.00",

                adjustment: currentAdjustment.toFixed(2),
                total_amount: currentTotal.toFixed(2),
                amount_due: (
                    currentTotal - parseFloat(invoiceData.amountPaid || 0)
                ).toFixed(2),
                payment_mode: invoiceData.paymentMode,
                payment_date: invoiceData.paymentDate,
                amount_paid: parseFloat(invoiceData.amountPaid || 0).toFixed(2),
                status: invoiceData.status,
            };

            let finalInvoiceMasterId = invoiceId;

            /* =====================================================
               1️⃣ INVOICE MASTER (CREATE / UPDATE)
            ===================================================== */

            if (invoiceId) {
                const data = await putData(
                    `api/invoice_master/${invoiceId}`,
                    payload
                );

                if (!data?.success) {
                    Swal.fire(
                        "Failed",
                        "Could not update invoice master. Please try again.",
                        "error"
                    );
                    return;
                }

                console.log("✅ Invoice master updated:", invoiceId);
            } else {
                const data = await postData("api/invoice_master", payload);

                if (!data?.success || !data?.id) {
                    Swal.fire(
                        "Failed",
                        `Could not save invoice master. Response: ${JSON.stringify(
                            data
                        )}`,
                        "error"
                    );
                    return;
                }

                finalInvoiceMasterId = data.id;
                console.log("✅ New invoice master created:", finalInvoiceMasterId);
            }

            /* =====================================================
               2️⃣ INVOICE ITEMS (INSERT / UPDATE / DELETE)
            ===================================================== */

            const itemsToInsert = items.filter((i) => !i.id);

            const itemsToUpdate = items.filter(
                (i) =>
                    i.id &&
                    originalItems.some(
                        (o) =>
                            o.id === i.id &&
                            (o.description !== i.description ||
                                o.sacCode !== i.sacCode ||
                                o.amount.toFixed(2) !== i.amount.toFixed(2))
                    )
            );

            const itemsToDelete = originalItems.filter(
                (o) => !items.some((i) => i.id === o.id)
            );

            for (const item of itemsToDelete) {
                await deleteInvoiceItem(item.id);
            }

            for (const item of itemsToUpdate) {
                await updateInvoiceItem(item);
            }

            for (const item of itemsToInsert) {
                await insertInvoiceItem(item, finalInvoiceMasterId);
            }

            Swal.fire({
                title: invoiceId ? "Invoice Updated!" : "Invoice Saved!",
                text: invoiceId
                    ? "Invoice and items updated successfully."
                    : "Invoice and items added successfully.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false,
            });

            setTimeout(() => navigate("/invoive_master"), 1500);
        } catch (err) {
            console.error("Save/Update error:", err);
            Swal.fire(
                "Error",
                "Something went wrong while saving/updating invoice.",
                "error"
            );
        }
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-xl font-semibold text-gray-700">Loading Invoice...</div>
            </div>
        );
    }

    return (
        <>

            <motion.div
                className="
    bg-white shadow-md rounded-lg
    p-6
    max-w-6xl
    mx-auto
    mt-20 
    ml-0 lg:ml-[340px]
  "
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
            >

                {/* Header */}
                <div className="flex justify-between items-center border-b pb-2 mb-4">
                    <h2 className="text-2xl font-semibold text-gray-800">
                        {invoiceId ? "Edit Invoice" : "New Invoice"}{" "}
                        <span className="text-blue-600">{invoiceData.invoiceNumber}</span>
                    </h2>
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <MdHome className="text-gray-600" /> Home &gt; Invoice
                    </div>
                </div>

                {/* Recipient Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <div>
                        <label className="font-medium text-gray-600">Invoice Number*</label>
                        <input
                            type="text"
                            value={invoiceData.invoiceNumber}
                            onChange={(e) => setInvoiceData({ ...invoiceData, invoiceNumber: e.target.value })}
                            className="border p-2 rounded w-full focus:ring-blue-500 focus:border-blue-500"
                            readOnly={!!invoiceId}
                        />
                    </div>
                    <div>
                        <label className="font-medium text-gray-600">Invoice Date*</label>
                        <input
                            type="date"
                            value={invoiceData.invoiceDate}
                            onChange={(e) => setInvoiceData({ ...invoiceData, invoiceDate: e.target.value })}
                            className="border p-2 rounded w-full focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="font-medium text-gray-600">Recipient Name*</label>
                        <select
                            value={invoiceData.recipientName}
                            onChange={handleCompanySelect}
                            className="border p-2 rounded w-full focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Select Company</option>
                            {companies.map((c) => (
                                <option key={c.id} value={c.name}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="font-medium text-gray-600">Recipient GSTIN*</label>
                        <input
                            type="text"
                            value={invoiceData.recipientGSTIN}
                            readOnly
                            className="border p-2 rounded w-full bg-gray-100 cursor-not-allowed"
                        />
                    </div>
                    <div>
                        <label className="font-medium text-gray-600">Recipient State*</label>
                        <input
                            type="text"
                            value={invoiceData.recipientState}
                            readOnly
                            className="border p-2 rounded w-full bg-gray-100 cursor-not-allowed"
                        />
                    </div>
                    <div>
                        <label className="font-medium text-gray-600">Recipient Address*</label>
                        <input
                            type="text"
                            value={invoiceData.recipientAddress}
                            readOnly
                            className="border p-2 rounded w-full bg-gray-100 cursor-not-allowed"
                        />
                    </div>

                    <div>
                        <label className="font-medium text-gray-600">Payment Mode*</label>
                        <select
                            value={invoiceData.paymentMode}
                            onChange={(e) => setInvoiceData({ ...invoiceData, paymentMode: e.target.value })}
                            className="border p-2 rounded w-full focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Select Payment Mode</option>
                            <option value="Cash">Cash</option>
                            <option value="Cheque">Cheque</option>
                            <option value="Online">Online</option>
                        </select>
                    </div>

                    <div>
                        <label className="font-medium text-gray-600">Invoice Type*</label>
                        <select
                            value={invoiceData.invoiceType}
                            onChange={(e) => setInvoiceData({ ...invoiceData, invoiceType: e.target.value })}
                            className="border p-2 rounded w-full focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="Inter State Invoice">Inter State Invoice</option>
                            <option value="Intra State Invoice">Intra State Invoice</option>
                        </select>
                    </div>

                    <div>
                        <label className="font-medium text-gray-600">Status</label>
                        <select
                            value={invoiceData.status}
                            onChange={(e) => setInvoiceData({ ...invoiceData, status: e.target.value })}
                            className="border p-2 rounded w-full focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="pending">Pending</option>
                            <option value="paid">Paid</option>
                            <option value="overdue">Overdue</option>
                        </select>
                    </div>

                    {invoiceData.status === "paid" && (
                        <>
                            <div>
                                <label className="font-medium text-gray-600">Payment Date</label>
                                <input
                                    type="date"
                                    value={invoiceData.paymentDate || ""}
                                    onChange={(e) => setInvoiceData({ ...invoiceData, paymentDate: e.target.value })}
                                    className="border p-2 rounded w-full focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label className="font-medium text-gray-600">Amount Paid</label>
                                <input
                                    type="number"
                                    value={invoiceData.amountPaid}
                                    onChange={(e) => setInvoiceData({ ...invoiceData, amountPaid: e.target.value })}
                                    className="border p-2 rounded w-full focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Items Table */}
                <table className="min-w-full border text-sm mb-4">
                    <thead className="bg-gray-100 text-gray-700">
                        <tr>
                            <th className="border p-2 w-12 text-center">S.No</th>
                            <th className="border p-2 text-left">Service Description</th>
                            <th className="border p-2 w-28 text-center">SAC Code</th>
                            <th className="border p-2 w-36 text-right">Taxable Amount</th>
                            <th className="border p-2 w-16 text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="text-center text-gray-400 p-4">
                                    No item added to list
                                </td>
                            </tr>
                        ) : (
                            items.map((item, index) => (
                                <tr key={item.id || `new-${index}`}>
                                    <td className="border p-2 text-center">{index + 1}</td>
                                    <td className="border p-2">{item.description}</td>
                                    <td className="border p-2 text-center">{item.sacCode}</td>
                                    <td className="border p-2 text-right">₹ {item.amount.toFixed(2)}</td>
                                    <td className="border p-2 text-center">
                                        <button
                                            onClick={() => removeItem(index)}
                                            className="text-red-500 hover:text-red-700 transition"
                                            title="Remove"
                                        >
                                            <FaTrashAlt />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {/* Add Item Inputs */}
                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                    <input
                        type="text"
                        placeholder="Service Description"
                        value={invoiceData.serviceDescription}
                        onChange={(e) =>
                            setInvoiceData({ ...invoiceData, serviceDescription: e.target.value })
                        }
                        className="border p-2 rounded w-full sm:w-auto flex-grow focus:ring-blue-500 focus:border-blue-500"
                    />
                    <input
                        type="text"
                        placeholder="SAC Code"
                        value={invoiceData.sacCode || "998519"}
                        onChange={(e) => setInvoiceData({ ...invoiceData, sacCode: e.target.value })}
                        className="border p-2 rounded w-full sm:w-32 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <input
                        type="number"
                        placeholder="Taxable Amount"
                        value={invoiceData.taxableAmount}
                        onChange={(e) => setInvoiceData({ ...invoiceData, taxableAmount: e.target.value })}
                        className="border p-2 rounded w-full sm:w-40 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                        onClick={addItem}
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition flex items-center justify-center gap-1 w-full sm:w-auto"
                    >
                        <FaPlus /> Add Item
                    </button>
                </div>

                {/* Totals Section */}
                <div className="flex justify-end mb-6">
                    <div className="w-full sm:w-80 text-right space-y-2">
                        <div>Subtotal: <span className="font-semibold">₹ {subtotal.toFixed(2)}</span></div>
                        <div className="flex justify-between items-center">
                            <span>Tax in (%):</span>{" "}
                            <input
                                type="number"
                                value={invoiceData.tax}
                                onChange={(e) => setInvoiceData({ ...invoiceData, tax: e.target.value })}
                                className="border p-1 rounded w-20 text-right focus:ring-blue-500 focus:border-blue-500"
                            />{" "}
                            <span className="font-semibold">₹ {taxAmount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span>Adjustment:</span>{" "}
                            <input
                                type="number"
                                value={invoiceData.adjustment}
                                onChange={(e) => setInvoiceData({ ...invoiceData, adjustment: e.target.value })}
                                className="border p-1 rounded w-20 text-right focus:ring-blue-500 focus:border-blue-500"
                            />{" "}
                            <span className="font-semibold">₹ {adjustmentValue.toFixed(2)}</span>
                        </div>
                        <div className="bg-blue-600 text-white font-bold text-lg p-3 rounded mt-2">
                            Total: ₹ {total.toFixed(2)}
                        </div>
                    </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3">
                    <button
                        onClick={() => navigate("/invoive_master")}
                        className="border border-gray-300 px-4 py-2 rounded text-gray-600 hover:bg-gray-100 transition flex items-center gap-2"
                    >
                        <FaTimes /> Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition flex items-center gap-2"
                    >
                        <FaSave /> {invoiceId ? "Update Invoice" : "Save Invoice"}
                    </button>

                </div>
            </motion.div>
        </>
    );
};

export default InvoiceForm;